import { inject, Injectable } from '@angular/core';
import { OrganizationState } from '../states/organization.state';
import { Store } from '@ngxs/store';
import { hasActionsExecuting } from '@ngxs-labs/actions-executing';
import * as OrganizationActions from '~console/app/~store/actions/organization.actions';
import { Observable } from 'rxjs';

@Injectable()
export class OrganizationFacade {
  protected _store = inject(Store);

  organizations$ = this._store.select(OrganizationState.organizations);
  organization$ = this._store.select(OrganizationState.selectedOrganization);
  organizationUsers$ = this._store.select(OrganizationState.organizationUsers);
  partners$ = this._store.select(OrganizationState.partners);
  preferences$ = this._store.select(OrganizationState.preferences);

  organization = this._store.selectSignal(
    OrganizationState.selectedOrganization
  );

  organizationUsers = this._store.selectSignal(
    OrganizationState.organizationUsers
  );

  partners = this._store.selectSignal(OrganizationState.partners);
  preferences = this._store.selectSignal(OrganizationState.preferences);

  readonly isLoadingOrganizations$ = this._store.select(
    hasActionsExecuting([OrganizationActions.LoadOrganizations])
  );

  readonly isLoadingOrganization$ = this._store.select(
    hasActionsExecuting([OrganizationActions.LoadOrganization])
  );

  readonly isCreatingOrganization$ = this._store.select(
    hasActionsExecuting([OrganizationActions.CreateOrganization])
  );

  readonly isUpdatingOrganization$ = this._store.select(
    hasActionsExecuting([OrganizationActions.UpdateOrganization])
  );

  readonly isDeletingOrganization$ = this._store.select(
    hasActionsExecuting([OrganizationActions.DeleteOrganization])
  );

  readonly isLoadingOrganizationUsers$ = this._store.select(
    hasActionsExecuting([OrganizationActions.LoadOrganizationUsers])
  );

  readonly isUpdatingOrganizationUser$ = this._store.select(
    hasActionsExecuting([OrganizationActions.UpdateOrganizationUser])
  );

  readonly isUpdatingOrganizationUserScopes$ = this._store.select(
    hasActionsExecuting([OrganizationActions.UpdateOrganizationUserScopes])
  );

  readonly isDeletingOrganizationUser$ = this._store.select(
    hasActionsExecuting([OrganizationActions.DeleteOrganizationUser])
  );

  readonly isUploadingLogo$ = this._store.select(
    hasActionsExecuting([OrganizationActions.UploadOrganizationLogo])
  );

  readonly isLoadingPartners$ = this._store.select(
    hasActionsExecuting([OrganizationActions.LoadPartners])
  );

  readonly isCreatingPartner$ = this._store.select(
    hasActionsExecuting([OrganizationActions.CreatePartner])
  );

  readonly isAddingPartner$ = this._store.select(
    hasActionsExecuting([OrganizationActions.AddPartnerInOrganization])
  );

  readonly isSearchingOrganization$ = this._store.select(
    hasActionsExecuting([OrganizationActions.PublicSearchOrganization])
  );

  readonly isLoadingPreferences$ = this._store.select(
    hasActionsExecuting([OrganizationActions.LoadOrganizationPreferences])
  );

  readonly isSettingPreferences$ = this._store.select(
    hasActionsExecuting([OrganizationActions.SetOrganizationPreferences])
  );

  loadOrganizations(
    ...params: ConstructorParameters<
      typeof OrganizationActions.LoadOrganizations
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.LoadOrganizations(...params)
    );
  }

  loadOrganization(
    ...params: ConstructorParameters<
      typeof OrganizationActions.LoadOrganization
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.LoadOrganization(...params)
    );
  }

  createOrganization(
    ...params: ConstructorParameters<
      typeof OrganizationActions.CreateOrganization
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.CreateOrganization(...params)
    );
  }

  updateOrganization(
    ...params: ConstructorParameters<
      typeof OrganizationActions.UpdateOrganization
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.UpdateOrganization(...params)
    );
  }

  patchOrganization(
    ...params: ConstructorParameters<
      typeof OrganizationActions.PatchOrganization
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.PatchOrganization(...params)
    );
  }

  deleteOrganization(
    ...params: ConstructorParameters<
      typeof OrganizationActions.DeleteOrganization
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.DeleteOrganization(...params)
    );
  }

  loadOrganizationUsers(
    ...params: ConstructorParameters<
      typeof OrganizationActions.LoadOrganizationUsers
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.LoadOrganizationUsers(...params)
    );
  }

  updateOrganizationUser(
    ...params: ConstructorParameters<
      typeof OrganizationActions.UpdateOrganizationUser
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.UpdateOrganizationUser(...params)
    );
  }

  updateOrganizationUserScopes(
    ...params: ConstructorParameters<
      typeof OrganizationActions.UpdateOrganizationUserScopes
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.UpdateOrganizationUserScopes(...params)
    );
  }

  deleteOrganizationUser(
    ...params: ConstructorParameters<
      typeof OrganizationActions.DeleteOrganizationUser
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.DeleteOrganizationUser(...params)
    );
  }

  uploadOrganizationLogo(
    ...params: ConstructorParameters<
      typeof OrganizationActions.UploadOrganizationLogo
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.UploadOrganizationLogo(...params)
    );
  }

  loadPartners(
    ...params: ConstructorParameters<typeof OrganizationActions.LoadPartners>
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.LoadPartners(...params)
    );
  }

  createPartner(
    ...params: ConstructorParameters<typeof OrganizationActions.CreatePartner>
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.CreatePartner(...params)
    );
  }

  addPartnerInOrganization(
    ...params: ConstructorParameters<
      typeof OrganizationActions.AddPartnerInOrganization
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.AddPartnerInOrganization(...params)
    );
  }

  publicSearchOrganization(
    ...params: ConstructorParameters<
      typeof OrganizationActions.PublicSearchOrganization
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.PublicSearchOrganization(...params)
    );
  }

  loadOrganizationPreferences(
    ...params: ConstructorParameters<
      typeof OrganizationActions.LoadOrganizationPreferences
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.LoadOrganizationPreferences(...params)
    );
  }

  setOrganizationPreferences(
    ...params: ConstructorParameters<
      typeof OrganizationActions.SetOrganizationPreferences
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.SetOrganizationPreferences(...params)
    );
  }

  clearSelected(
    ...params: ConstructorParameters<
      typeof OrganizationActions.ClearSelectedOrganization
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.ClearSelectedOrganization(...params)
    );
  }

  reset(
    ...params: ConstructorParameters<
      typeof OrganizationActions.ResetOrganizationState
    >
  ): Observable<unknown> {
    return this._store.dispatch(
      new OrganizationActions.ResetOrganizationState(...params)
    );
  }
}
